import {
  buildInmateSummary,
  GlobalInmateSearchResponse,
  GlobalSearchContext,
  GlobalSearchTrayColumn,
  GlobalSearchTrayPayload,
  GlobalSearchTrayRow,
} from "@app/shared/services/global-search.models";
import { personFirstNames, personLastName } from "@app/shared/util/person-name";

import { ProceedingCalendarRow, ReadyToScheduleRow } from "./hearing-schedule.models";

/**
 * Global-search override for the Hearing Schedule page. The top-nav inmate search keeps returning all
 * matching inmates. A result that is eligible to schedule — present in the ready-to-schedule backlog,
 * i.e. not yet on the calendar — gets an "Add to Calendar" action that places the hearing. A result that
 * already has an upcoming scheduled hearing instead gets a "View in Calendar" action that jumps the
 * calendar to the month of that hearing. Registered while the page is mounted and cleared on destroy, so
 * every other page keeps the default "Open" behavior.
 */
export class HearingScheduleSearchContext implements GlobalSearchContext {
  constructor(
    private readonly findCandidate: (incarceratedPersonId: number) => ReadyToScheduleRow | undefined,
    private readonly onAdd: (candidate: ReadyToScheduleRow) => void,
    private readonly findScheduled: (incarceratedPersonId: number) => ProceedingCalendarRow | undefined,
    private readonly onViewMonth: (hearing: ProceedingCalendarRow) => void,
  ) {}

  buildPayload(response: GlobalInmateSearchResponse): GlobalSearchTrayPayload {
    const rows: GlobalSearchTrayRow[] = response.results.map((row) => ({
      ...row,
      summary: buildInmateSummary(row),
    }));

    return {
      results: rows,
      model: this.columns(),
      totalCount: response.totalCount,
      recordCount: rows.length,
    };
  }

  private columns(): GlobalSearchTrayColumn[] {
    return [
      {
        // Not a row property on purpose: when getValue returns "" the tray falls back to
        // row[name], and an unknown key renders blank instead of leaking the raw display name.
        name: "personLastName",
        displayName: "Last Name",
        type: "string",
        wrapText: "wrapText",
        // Render-time split of the raw display name (#1436); the row keeps the raw value.
        options: { getValue: (row) => personLastName(row.displayName) },
      },
      {
        name: "personFirstNames",
        displayName: "First Name",
        type: "string",
        wrapText: "wrapText",
        options: { getValue: (row) => personFirstNames(row.displayName) },
      },
      { name: "inmateNumber", displayName: "Inmate #", type: "string" },
      { name: "matchSummary", displayName: "Matched on", type: "string", wrapText: "wrapText" },
      { name: "summary", displayName: "Summary", type: "string", wrapText: "wrapText" },
      {
        name: "schedule",
        displayName: "",
        type: "httpaction",
        thAlign: "alignCenter",
        tdAlign: "alignCenter",
        options: {
          buttonText: "Add to Calendar",
          // Only eligible (ready-to-schedule) inmates can be placed; hide the button for the rest.
          isHidden: (row) => !this.findCandidate(row.incarceratedPersonId),
          clickHandler: (row) => {
            const candidate = this.findCandidate(row.incarceratedPersonId);
            if (candidate) {
              this.onAdd(candidate);
            }
          },
        },
      },
      {
        name: "viewMonth",
        displayName: "",
        type: "httpaction",
        thAlign: "alignCenter",
        tdAlign: "alignCenter",
        options: {
          buttonText: "View in Calendar",
          // Shown only for inmates who already have an upcoming scheduled hearing.
          isHidden: (row) => !this.findScheduled(row.incarceratedPersonId),
          clickHandler: (row) => {
            const hearing = this.findScheduled(row.incarceratedPersonId);
            if (hearing) {
              this.onViewMonth(hearing);
            }
          },
        },
      },
      {
        name: "detailUrl",
        displayName: "",
        type: "link",
        thAlign: "alignCenter",
        tdAlign: "alignCenter",
        options: {
          text: "Open",
          routerLink: (row) => ["/case-context/inmate", row.incarceratedPersonId],
        },
      },
    ];
  }
}
